import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { getAppraisalRevisionReasons, requestAppraisalRevision } from 'services'
import { Button, Checkbox, TextArea } from 'stories/components'

import { Appraisal2StatusType, IntegrationType, RevisionReasonGroup } from '../types'

interface SelectedReason {
  reasonType: string
  reason: string
}

export const RequestRevisionTab = ({ appraisalID, status }: { appraisalID: number; status?: string | null }) => {
  const [reasonGroups, setReasonGroups] = useState<RevisionReasonGroup[]>([])
  const [loadingReasons, setLoadingReasons] = useState(true)
  const [selected, setSelected] = useState<SelectedReason[]>([])
  const [submitting, setSubmitting] = useState(false)
  const isCompleted = status === 'completed'

  useEffect(() => {
    ;(async () => {
      setLoadingReasons(true)
      const res: any = await getAppraisalRevisionReasons(IntegrationType.ClassValuation)
      if (res.success) setReasonGroups(res.data || [])
      setLoadingReasons(false)
    })()
  }, [])

  const findSelected = (key: string) => selected.find((item) => item.reasonType === key)

  const toggleReason = (key: string, checked: boolean) => {
    if (checked) setSelected((prev) => [...prev, { reasonType: key, reason: '' }])
    else setSelected((prev) => prev.filter((item) => item.reasonType !== key))
  }

  const updateReasonNote = (key: string, note: string) => {
    setSelected((prev) => prev.map((item) => (item.reasonType === key ? { ...item, reason: note } : item)))
  }

  const submit = async () => {
    if (!isCompleted) {
      toast('Revisions can only be requested once the order has been Completed.', { type: 'warning' })
      return
    }
    if (!selected.length) {
      toast('Please select at least one revision reason.', { type: 'warning' })
      return
    }
    setSubmitting(true)
    const res: any = await requestAppraisalRevision({ appraisalID, reasons: selected }, IntegrationType.ClassValuation)
    setSubmitting(false)
    if (res.success) {
      toast('Revision request sent to Class Valuation.', { type: 'success' })
      setSelected([])
    } else {
      toast(res.message || 'Failed to request revision.', { type: 'error' })
    }
  }

  return (
    <div>
      <div className="text-sm text-gray-500 mb-3">
        Select one or more reasons for requesting a revision on this order. You can add extra detail for each reason
        selected below.
      </div>
      {!isCompleted && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Revisions can only be requested once this order has reached <span className="font-bold">Completed</span>{' '}
          status.
          {status ? ` Current status: ${Appraisal2StatusType[status] || status}.` : ''}
        </div>
      )}
      {loadingReasons ? (
        <div className="text-sm text-gray-400 italic py-4 text-center">Loading revision reasons...</div>
      ) : (
        <div className="max-h-96 overflow-y-auto border rounded p-2">
          {reasonGroups.map((group) => (
            <details key={group.category} className="border-b last:border-b-0 py-1">
              <summary className="cursor-pointer font-bold text-sm text-gray-700 py-1">{group.category}</summary>
              <div className="pl-2 pb-2">
                {group.options.map((option) => {
                  const item = findSelected(option.key)
                  return (
                    <div key={option.key} className="py-1">
                      <Checkbox
                        id={`revision-reason-${option.key}`}
                        title={option.label}
                        value={!!item}
                        onChange={(value) => toggleReason(option.key, value)}
                      />
                      {item && (
                        <div className="pl-6 pt-1">
                          <TextArea
                            title="Additional detail (optional)"
                            value={item.reason}
                            onChange={(value) => updateReasonNote(option.key, value)}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </details>
          ))}
        </div>
      )}
      <div className="text-center mt-4">
        <Button color="gray" onClick={submit} loading={submitting} disabled={!isCompleted || selected.length === 0}>
          Send Revision Request
        </Button>
      </div>
    </div>
  )
}
